/**
 * Icon kit — the single source of truth for line icons in the New UI.
 *
 * Why this exists: the app historically used colourful emoji as UI icons, which
 * render inconsistently across platforms and clash with the refined brand look.
 * This module maps stable SEMANTIC names (not emoji) to monochrome Tabler line
 * icons (react-icons/tb) and frames them in the same rounded "tile" treatment
 * introduced in ExportMenu — so every surface shares one icon language.
 *
 *   <Icon name="legal" />                     → bare line icon
 *   <IconTile name="llp" tone="#1a4f8a" />    → icon in a tinted rounded tile
 *
 * Call sites pass a semantic name; if a name is unknown the raw string is
 * rendered as-is, so an un-migrated emoji still shows (graceful degradation) and
 * the rollout can proceed surface-by-surface without breaking a page.
 */
import {
  TbBuilding, TbBuildingSkyscraper, TbBuildingBank, TbBuildingCommunity,
  TbHeartHandshake, TbUsers, TbUsersGroup, TbUser, TbTrendingUp, TbPlant2,
  TbRocket, TbWorld, TbStethoscope, TbSchool, TbClipboardList,
  TbClipboardCheck, TbBriefcase, TbChartBar, TbReportAnalytics, TbLock,
  TbLockOpen, TbShieldCheck, TbScale, TbCalendarEvent, TbZoomCheck, TbTarget,
  TbCoins, TbCurrencyRupee, TbRobot, TbCreditCard, TbHeadset, TbRefresh,
  TbFileText, TbAlertTriangle, TbCircleCheck, TbCircleX, TbBan, TbInfoCircle,
  TbHandStop, TbClock, TbBell, TbHourglassHigh, TbBolt, TbSearch,
  TbPencil, TbTicket, TbHelpCircle, TbMessageCircle, TbShieldLock, TbId,
  TbKey, TbDeviceDesktop, TbTie, TbFlag, TbClockHour4, TbPlayerPause,
  TbPackage, TbMail, TbMailOpened, TbDownload, TbUpload, TbEye, TbCrown,
  TbSparkles, TbPin, TbSignature, TbAward, TbFiles, TbScript,
  TbSpeakerphone, TbDoorExit,
} from 'react-icons/tb';

// Semantic name → line-icon component. Grouped by domain; several names may map
// to the same glyph when they mean the same thing in different contexts.
export const ICONS = {
  // ── Entity / company types ──────────────────────────────────────────────
  'pvt-ltd': TbBuilding,
  company: TbBuilding,
  fiduciary: TbBuilding,
  llp: TbHeartHandshake,
  'public-co': TbTrendingUp,
  opc: TbUser,
  ngo: TbPlant2,
  partnership: TbUsersGroup,
  nbfc: TbBuildingBank,
  governance: TbBuildingBank,
  healthcare: TbStethoscope,
  education: TbSchool,
  'real-estate': TbBuildingCommunity,
  startup: TbRocket,
  'foreign-subsidiary': TbWorld,
  global: TbWorld,
  portfolio: TbBuildingSkyscraper,

  // ── Compliance / governance concepts ────────────────────────────────────
  people: TbUsers,
  directors: TbUsers,
  capital: TbBriefcase,
  business: TbBriefcase,
  'board-meeting': TbClipboardList,
  documents: TbClipboardList,
  document: TbFileText,
  meeting: TbUsersGroup,
  agm: TbUsersGroup,
  reporting: TbChartBar,
  analytics: TbChartBar,
  audit: TbReportAnalytics,
  security: TbLock,
  lock: TbLock,
  unlock: TbLockOpen,
  shield: TbShieldCheck,
  rights: TbShieldCheck,
  legal: TbScale,
  calendar: TbCalendarEvent,
  deadline: TbCalendarEvent,
  review: TbZoomCheck,
  search: TbSearch,
  goal: TbTarget,
  objective: TbTarget,
  money: TbCoins,
  penalty: TbCoins,
  rupee: TbCurrencyRupee,
  // Vee is NOT a robot — sparkles until the redesigned mascot glyph lands.
  ai: TbSparkles,
  payment: TbCreditCard,
  support: TbHeadset,
  conversion: TbRefresh,
  consent: TbClipboardCheck,

  // ── Status / semantic ───────────────────────────────────────────────────
  success: TbCircleCheck,
  error: TbAlertTriangle,
  warning: TbAlertTriangle,
  critical: TbAlertTriangle,
  info: TbInfoCircle,
  pending: TbHourglassHigh,
  due: TbHourglassHigh,

  // ── Notifications ───────────────────────────────────────────────────────
  assigned: TbClipboardList,
  accepted: TbCircleCheck,
  approved: TbCircleCheck,
  rejected: TbCircleX,
  revoked: TbBan,
  denied: TbBan,
  'access-granted': TbLockOpen,
  'access-revoked': TbLock,
  requested: TbHandStop,
  reminder: TbClock,
  bell: TbBell,
  system: TbBell,

  // ── Actions ─────────────────────────────────────────────────────────────
  'quick-actions': TbBolt,
  bolt: TbBolt,
  edit: TbPencil,
};

// Back-compat: legacy data arrays across the app still hold emoji. Map each to a
// line icon so those surfaces migrate by changing only the render site, not the
// data. Keys are the BASE emoji — variation selectors (U+FE0F) are stripped
// before lookup, so '🏛' and '🏛️' both resolve. Prefer a semantic ICONS name in
// new code; these aliases exist only to retire the emoji in place.
const EMOJI_ALIASES = {
  '🏗': TbBuildingSkyscraper, '🏢': TbBuilding, '🏛': TbBuildingBank,
  '🏦': TbBuildingBank, '🏥': TbStethoscope, '🏫': TbSchool, '💻': TbRocket,
  '🤝': TbHeartHandshake, '📈': TbTrendingUp, '👤': TbUser, '👥': TbUsers,
  '🌱': TbPlant2, '🌍': TbWorld, '📋': TbClipboardList, '⚖': TbScale,
  '📅': TbCalendarEvent, '🔍': TbZoomCheck, '💼': TbBriefcase, '📊': TbChartBar,
  '🔒': TbLock, '🔓': TbLockOpen, '🛡': TbShieldCheck, '📄': TbFileText,
  '🎯': TbTarget, '💰': TbCoins, '🤖': TbSparkles, '💳': TbCreditCard,
  '📞': TbHeadset, '🔄': TbRefresh, '✅': TbCircleCheck, '⏳': TbHourglassHigh,
  '🚨': TbAlertTriangle, '⚡': TbBolt, '⚠': TbAlertTriangle, 'ℹ': TbInfoCircle,
  '❌': TbCircleX, '🚫': TbBan, '⛔': TbBan, '🙋': TbHandStop, '⏰': TbClock,
  '🔔': TbBell, '📝': TbPencil, '✏': TbPencil, '🎫': TbTicket,
  '❓': TbHelpCircle, '💬': TbMessageCircle, '🎓': TbSchool,
  '🔐': TbShieldLock, '🪪': TbId, '🔑': TbKey, '🖥': TbDeviceDesktop,
  '👔': TbTie, '🚩': TbFlag, '🕐': TbClockHour4, '⏸': TbPlayerPause,
  '📦': TbPackage, '📨': TbMail, '✉': TbMail, '📬': TbMailOpened,
  '📥': TbDownload, '📤': TbUpload, '👁': TbEye, '👑': TbCrown,
  '✨': TbSparkles, '📌': TbPin, '✍': TbSignature, '🏅': TbAward,
  '📑': TbFiles, '📜': TbScript, '📣': TbSpeakerphone, '🚀': TbRocket,
  '🚪': TbDoorExit, '🌐': TbWorld,
};

const TILE_SIZES = {
  xs: { box: 24, icon: 13, radius: 7 },
  sm: { box: 28, icon: 15, radius: 8 },
  md: { box: 38, icon: 19, radius: 10 },
  lg: { box: 46, icon: 22, radius: 12 },
  xl: { box: 56, icon: 27, radius: 14 },
};

/**
 * Bare line icon resolved from a semantic `name` (or an explicit `icon`
 * component). Unknown names fall back to rendering the raw string so a
 * not-yet-migrated emoji still appears instead of vanishing.
 */
export function Icon({ name, icon: IconComp, size = 18, className = '', style }) {
  // Strip emoji variation selectors so '🏛' and '🏛️' map to the same alias.
  const key = typeof name === 'string' ? name.split('️').join('') : name;
  const Resolved = IconComp || (key && (ICONS[key] || EMOJI_ALIASES[key]));
  if (Resolved) {
    return <Resolved size={size} className={className} style={style} aria-hidden="true" />;
  }
  if (typeof name === 'string' && name) {
    return (
      <span className={className} style={{ fontSize: size, lineHeight: 1, ...style }} aria-hidden="true">
        {name}
      </span>
    );
  }
  return null;
}

/**
 * `Icon` framed in a rounded tile. `tone` is either 'neutral' (parchment) or a
 * brand hex colour, in which case the tile is a 10% wash of that colour with the
 * icon in the full colour. `interactive` tints the tile teal on a parent
 * :hover — pair it with a `group` class on the hoverable ancestor.
 */
export function IconTile({
  name, icon, size = 'md', tone = 'neutral', interactive = false, className = '', style,
}) {
  const s = TILE_SIZES[size] || TILE_SIZES.md;
  const neutral = !tone || tone === 'neutral';
  const bg = neutral ? '#f3efe5' : `${tone}1a`;
  const fg = neutral ? '#6b6354' : tone;
  return (
    <span
      className={`cs-icon-tile inline-grid place-items-center shrink-0${interactive ? ' cs-icon-tile--interactive' : ''}${className ? ` ${className}` : ''}`}
      style={{
        width: s.box, height: s.box, borderRadius: s.radius,
        background: bg, color: fg, transition: 'background .15s, color .15s',
        ...style,
      }}
      aria-hidden="true"
    >
      <Icon name={name} icon={icon} size={s.icon} />
    </span>
  );
}

export default Icon;
